import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Tag, Modal, Form, Input, Select, message, Typography, Popconfirm, Row, Col, Grid, Upload, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const PartnersPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [partners, setPartners] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isImportVisible, setIsImportVisible] = useState(false);
    const [importRows, setImportRows] = useState([]);
    const [importFileName, setImportFileName] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [form] = Form.useForm();
    const partnerType = Form.useWatch('type', form);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const [partnersRes, branchesRes] = await Promise.all([
                client.get('/master/partners'),
                client.get('/master/branches'),
            ]);
            setPartners(partnersRes.data);
            setBranches(branchesRes.data || []);
        } catch (error) {
            message.error('Failed to fetch partners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true });
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        const [legacyLast = '', legacyGiven = ''] = String(record.name || '').split(',', 2);
        form.setFieldsValue({
            ...record,
            last_name: record.last_name || legacyLast.trim(),
            first_name: record.first_name || legacyGiven.trim(),
            middle_name: record.middle_name || '',
        });
        setIsModalVisible(true);
    };

    const deactivatePartner = async (record) => {
        try {
            const payload = {
                ...record,
                rice_allowance_kg: record.type === 'EMPLOYEE' ? Number(record.rice_allowance_kg || 25) : null,
                is_active: false,
            };
            await client.put(`/master/partners/${record.id}`, payload);
            message.success('Partner set to inactive');
            fetchPartners();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to deactivate partner');
        }
    };

    const handleDelete = async (record) => {
        try {
            await client.delete(`/master/partners/${record.id}`);
            message.success('Partner deleted successfully');
            fetchPartners();
        } catch (error) {
            if (error.response?.status === 409) {
                Modal.confirm({
                    title: 'Partner linked to transactions',
                    content: 'This partner has existing transactions and cannot be deleted. Set to inactive instead?',
                    okText: 'Set Inactive',
                    cancelText: 'Cancel',
                    onOk: () => deactivatePartner(record),
                });
                return;
            }
            message.error(error.response?.data?.message || 'Failed to delete partner');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/partners/${editingId}`, values);
                message.success('Partner updated successfully');
            } else {
                await client.post('/master/partners', values);
                message.success('Partner created successfully');
            }
            setIsModalVisible(false);
            fetchPartners();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const downloadImportTemplate = () => {
        const partnerRows = [
            ['code', 'last_name', 'first_name', 'middle_name', 'type', 'branch_code', 'rice_allowance_kg', 'address', 'phone', 'email', 'is_active'],
            ['', 'Dela Cruz', 'Juan', 'Santos', 'EMPLOYEE', branches[0]?.code || 'MAIN', 25, '', '', '', 'TRUE'],
            ['', 'Reyes', 'Maria', '', 'EMPLOYEE', branches[0]?.code || 'MAIN', 50, '', '', '', 'TRUE'],
            ['', 'Garcia', 'Pedro', 'Lopez', 'CUSTOMER', '', '', '', '', '', 'TRUE'],
        ];
        const instructions = [
            ['Partners Bulk Import Instructions'],
            ['Required columns', 'last_name, first_name, type'],
            ['Partner code', 'Optional. Leave blank for automatic code generation; enter an existing code only when updating'],
            ['Allowed types', 'SUPPLIER, CUSTOMER, HAULER, EMPLOYEE, FARMER'],
            ['Employee allowance', 'Required for EMPLOYEE; enter 25 or 50'],
            ['Branch code', 'Use a code from Branches; optional for employees'],
            ['Existing code', 'A matching partner code will be updated'],
            ['Import behavior', 'All rows are validated first; no rows are saved if any row has an error'],
            ['Tip', 'Format the code column as Text when codes contain leading zeroes'],
        ];
        const workbook = XLSX.utils.book_new();
        const partnerSheet = XLSX.utils.aoa_to_sheet(partnerRows);
        partnerSheet['!cols'] = [14, 20, 20, 20, 14, 16, 20, 30, 18, 28, 12].map(width => ({ width }));
        XLSX.utils.book_append_sheet(workbook, partnerSheet, 'Partners');
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(instructions), 'Instructions');
        XLSX.writeFile(workbook, 'partners_import_template.xlsx');
    };

    const parseActive = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        return !['0', 'false', 'no', 'inactive'].includes(String(value || 'true').trim().toLowerCase());
    };

    const parseImportFile = async (file) => {
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
            if (!rawRows.length) throw new Error('The file does not contain any partner rows.');

            const normalizeObject = (row) => Object.fromEntries(Object.entries(row).map(([key, value]) => [
                String(key).trim().toLowerCase().replace(/\s+/g, '_'), value,
            ]));
            const valueOf = (row, keys) => keys.map(key => row[key]).find(value => value !== undefined) ?? '';
            const branchCodes = new Set(branches.map(branch => String(branch.code).trim().toUpperCase()));

            const parsed = rawRows.map((source, index) => {
                const row = normalizeObject(source);
                const code = String(valueOf(row, ['code', 'partner_code'])).trim();
                const type = String(valueOf(row, ['type', 'partner_type'])).trim().toUpperCase();
                const branchCode = String(valueOf(row, ['branch_code', 'branch'])).trim().toUpperCase();
                const allowanceRaw = valueOf(row, ['rice_allowance_kg', 'allowance_kg', 'rice_allowance']);
                const allowance = allowanceRaw === '' ? null : Number(allowanceRaw);
                return {
                    key: index + 2,
                    line: index + 2,
                    code,
                    last_name: String(valueOf(row, ['last_name', 'surname'])).trim(),
                    first_name: String(valueOf(row, ['first_name', 'given_name'])).trim(),
                    middle_name: String(valueOf(row, ['middle_name', 'middle'])).trim(),
                    type,
                    branch_code: branchCode,
                    rice_allowance_kg: allowance,
                    address: String(valueOf(row, ['address'])).trim(),
                    phone: String(valueOf(row, ['phone', 'contact'])).trim(),
                    email: String(valueOf(row, ['email'])).trim(),
                    is_active: parseActive(valueOf(row, ['is_active', 'active', 'status'])),
                    errors: [],
                    branchCodes,
                };
            });

            const codeLines = {};
            parsed.forEach(row => {
                if (row.code) (codeLines[row.code.toUpperCase()] ||= []).push(row.line);
            });
            const validTypes = ['SUPPLIER', 'CUSTOMER', 'HAULER', 'EMPLOYEE', 'FARMER'];
            const nameLines = {};
            parsed.forEach(row => {
                const key = `${row.type}|${row.last_name}, ${row.first_name} ${row.middle_name}`.trim().toLowerCase().replace(/\s+/g, ' ');
                if (row.last_name && row.first_name) (nameLines[key] ||= []).push(row.line);
            });
            const validatedRows = parsed.map(({ branchCodes: knownBranches, ...row }) => {
                const errors = [];
                if (!row.last_name) errors.push('Last name is required');
                if (!row.first_name) errors.push('First name is required');
                if (!validTypes.includes(row.type)) errors.push('Invalid partner type');
                if (row.code && codeLines[row.code.toUpperCase()]?.length > 1) errors.push('Duplicate code in file');
                const nameKey = `${row.type}|${row.last_name}, ${row.first_name} ${row.middle_name}`.trim().toLowerCase().replace(/\s+/g, ' ');
                if (row.last_name && row.first_name && nameLines[nameKey]?.length > 1) errors.push('Duplicate full name and type in file');
                if (row.branch_code && !knownBranches.has(row.branch_code)) errors.push('Unknown branch code');
                if (row.type === 'EMPLOYEE' && ![25, 50].includes(row.rice_allowance_kg)) errors.push('Allowance must be 25 or 50 kg');
                if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push('Invalid email');
                return { ...row, errors };
            });

            setImportRows(validatedRows);
            setImportFileName(file.name);
        } catch (error) {
            setImportRows([]);
            setImportFileName('');
            message.error(error.message || 'Unable to read the import file');
        }
        return false;
    };

    const submitImport = async () => {
        if (!importRows.length) return message.error('Choose a file to import.');
        if (importRows.some(row => row.errors.length)) return message.error('Fix the file errors before importing.');
        setImportLoading(true);
        try {
            const rows = importRows.map(({ key, line, errors, ...row }) => row);
            const { data } = await client.post('/master/partners/import', { rows });
            message.success(`Import complete: ${data.created} created, ${data.updated} updated.`);
            setIsImportVisible(false);
            setImportRows([]);
            setImportFileName('');
            fetchPartners();
        } catch (error) {
            const responseErrors = error.response?.data?.errors;
            if (Array.isArray(responseErrors)) {
                setImportRows(rows => rows.map(row => {
                    const match = responseErrors.find(item => item.row === row.line);
                    return match ? { ...row, errors: match.errors } : row;
                }));
            }
            message.error(error.response?.data?.message || 'Partner import failed');
        } finally {
            setImportLoading(false);
        }
    };

    const importColumns = [
        { title: 'Row', dataIndex: 'line', width: 65 },
        { title: 'Code', dataIndex: 'code', width: 130, render: value => value || <Typography.Text type="secondary">Automatic</Typography.Text> },
        { title: 'Last Name', dataIndex: 'last_name', width: 145 },
        { title: 'First Name', dataIndex: 'first_name', width: 145 },
        { title: 'Middle Name', dataIndex: 'middle_name', width: 135, render: value => value || '—' },
        { title: 'Type', dataIndex: 'type', width: 115 },
        { title: 'Branch', dataIndex: 'branch_code', width: 100, render: value => value || '—' },
        { title: 'Allowance', dataIndex: 'rice_allowance_kg', width: 105, render: value => value ? `${value} kg` : '—' },
        { title: 'Status', dataIndex: 'is_active', width: 90, render: value => <Tag color={value ? 'success' : 'default'}>{value ? 'Active' : 'Inactive'}</Tag> },
        { title: 'Validation', dataIndex: 'errors', width: 240, render: errors => errors.length ? <Typography.Text type="danger">{errors.join('; ')}</Typography.Text> : <Tag color="success">Ready</Tag> },
    ];

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                let color = 'default';
                if (type === 'SUPPLIER') color = 'blue';
                if (type === 'CUSTOMER') color = 'green';
                if (type === 'FARMER') color = 'gold';
                if (type === 'HAULER') color = 'purple';
                if (type === 'EMPLOYEE') color = 'cyan';
                return <Tag color={color} style={{ borderRadius: 12, padding: '0 12px' }}>{type}</Tag>;
            },
        },
        {
            title: 'Contact',
            key: 'contact',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography.Text style={{ fontSize: 13 }}><i className="fa-solid fa-phone" style={{ marginRight: 6, fontSize: 11, color: 'var(--text-secondary)' }}></i>{record.phone || 'N/A'}</Typography.Text>
                    {record.email && <Typography.Text type="secondary" style={{ fontSize: 12 }}><i className="fa-solid fa-envelope" style={{ marginRight: 6, fontSize: 11 }}></i>{record.email}</Typography.Text>}
                </div>
            )
        },
        {
            title: 'Rice Allowance',
            key: 'rice_allowance',
            render: (_, record) => record.type === 'EMPLOYEE'
                ? `${Number(record.rice_allowance_kg || 25)} kg${record.branch?.name ? ` · ${record.branch.name}` : ''}`
                : '—',
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Partner"
                        description="Are you sure you want to delete this partner?"
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const stats = {
        total: partners.length,
        farmers: (partners || []).filter(p => p.type === 'FARMER' && p.is_active).length,
        suppliers: (partners || []).filter(p => p.type === 'SUPPLIER' && p.is_active).length,
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Partners Management</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage relationships with farmers, suppliers, and customers.</Typography.Text>
                </div>
                {isAdmin && (
                    <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                        <Button icon={<UploadOutlined />} size="large" onClick={() => { setImportRows([]); setImportFileName(''); setIsImportVisible(true); }} style={{ height: 45, borderRadius: 10, fontWeight: 600 }}>Bulk Import</Button>
                        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={handleAdd} style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}>Add Partner</Button>
                    </Space>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Partners"
                        value={stats.total}
                        icon={<i className="fa-solid fa-users"></i>}
                        color="#3b82f6"
                        subText="Consolidated directory"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Farmers"
                        value={stats.farmers}
                        icon={<i className="fa-solid fa-tractor"></i>}
                        color="#10b981"
                        subText="Producers in network"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Suppliers"
                        value={stats.suppliers}
                        icon={<i className="fa-solid fa-truck-field"></i>}
                        color="#8b5cf6"
                        subText="Input & equipment sources"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={partners}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1000 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Partner' : 'Create Customer / Partner'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={600}
                className="premium-modal"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ is_active: true }}
                >
                    <Alert type="info" showIcon message="Partner code is generated automatically when you save." style={{ marginBottom: 16 }} />
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="last_name" label="Last Name" rules={[{ required: true, message: 'Last name is required' }]}>
                                <Input placeholder="e.g., Daroy" style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="first_name" label="First Name" rules={[{ required: true, message: 'First name is required' }]}>
                                <Input placeholder="e.g., John Paul" style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="middle_name" label="Middle Name">
                                <Input placeholder="Optional" style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="Partner Type"
                                rules={[{ required: true, message: 'Type is required' }]}
                            >
                                <Select style={{ height: 40 }}>
                                    <Option value="SUPPLIER">Supplier</Option>
                                    <Option value="CUSTOMER">Customer</Option>
                                    <Option value="FARMER">Farmer</Option>
                                    <Option value="HAULER">Hauler</Option>
                                    <Option value="EMPLOYEE">Employee</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label="Phone Number">
                                <Input style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {partnerType === 'EMPLOYEE' && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="branch_id" label="Employee Branch">
                                    <Select allowClear options={branches.map(branch => ({ label: branch.name, value: branch.id }))} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="rice_allowance_kg" label="Default Rice Allowance" rules={[{ required: true, message: 'Set the default allowance' }]}>
                                    <Select options={[{ label: '25 kg', value: 25 }, { label: '50 kg', value: 50 }]} />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Form.Item name="address" label="Address">
                        <Input.TextArea rows={3} style={{ borderRadius: 8 }} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={14}>
                            <Form.Item
                                name="email"
                                label="Email Address"
                                rules={[{ type: 'email' }]}
                            >
                                <Input style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="is_active" label="Account Status">
                                <Select style={{ height: 40 }}>
                                    <Option value={true}>Active</Option>
                                    <Option value={false}>Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Create Partner'}
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={<Space><FileExcelOutlined style={{ color: '#16a34a' }} />Bulk Import Partners</Space>}
                open={isImportVisible}
                onCancel={() => setIsImportVisible(false)}
                width={1100}
                okText={`Import ${importRows.length} Partner${importRows.length === 1 ? '' : 's'}`}
                onOk={submitImport}
                confirmLoading={importLoading}
                okButtonProps={{ disabled: !importRows.length || importRows.some(row => row.errors.length > 0) }}
            >
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Existing partner codes will be updated. The import is all-or-nothing: if any row is invalid, nothing is saved." />
                <Space wrap style={{ marginBottom: 16 }}>
                    <Upload accept=".xlsx,.xls,.csv" maxCount={1} showUploadList={false} beforeUpload={parseImportFile}>
                        <Button icon={<UploadOutlined />}>Choose Excel or CSV</Button>
                    </Upload>
                    <Button icon={<DownloadOutlined />} onClick={downloadImportTemplate}>Download Template</Button>
                    {importFileName && <Typography.Text type="secondary">Selected: {importFileName}</Typography.Text>}
                </Space>
                <Table columns={importColumns} dataSource={importRows} rowKey="key" size="small" pagination={{ pageSize: 8 }} scroll={{ x: 1380 }} locale={{ emptyText: 'Download the template, complete it, then choose the Excel or CSV file.' }} />
            </Modal>
        </div>
    );
};

export default PartnersPage;




